// Custom VITE_ env vars aren't in vite/client's ImportMetaEnv, so this is `any`
// until declared — narrow it to a string at the boundary.
export const apiBaseUrl =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  'http://localhost:8787'

import { createIsomorphicFn } from '@tanstack/react-start'
import { getRequestHeader } from '@tanstack/react-start/server'

/**
 * On SSR loads the loader's fetch runs on the server, which does NOT carry
 * the browser's cookies — every API call would 401 and bounce the user to
 * /login even though their session is valid. This forwards the incoming
 * request's cookie header to the API during SSR; in the browser it's a
 * no-op (credentials: 'include' does the job there). createIsomorphicFn
 * compiles the server branch (and its server-only import) out of the
 * client bundle.
 */
const readIncomingCookie = createIsomorphicFn()
  .server(() => getRequestHeader('cookie'))
  .client(() => undefined)

export async function buildApiHeaders(init?: RequestInit): Promise<Headers> {
  // Headers-object normalization: callers may pass a plain object, a
  // Headers instance, or string pairs — new Headers() handles all three,
  // where object-spreading would silently drop the non-plain shapes.
  const headers = new Headers(init?.headers)
  if (!headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json')
  const ssr = await ssrCookieHeaders()
  if (
    ssr.cookie !== undefined &&
    !headers.has('cookie') &&
    isTrustedApiOrigin(apiBaseUrl)
  )
    headers.set('cookie', ssr.cookie)
  return headers
}

/** Session cookies are only ever forwarded to our own API origins — a
 * misconfigured VITE_API_URL must fail closed (no cookie), not leak the
 * operator's session to a foreign host. */
export function isTrustedApiOrigin(base: string): boolean {
  try {
    const { hostname } = new URL(base)
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === 'stoaria.ai' ||
      hostname.endsWith('.stoaria.ai')
    )
  } catch {
    return false
  }
}

export async function ssrCookieHeaders(): Promise<Record<string, string>> {
  try {
    const cookie = await Promise.resolve(readIncomingCookie())
    return cookie ? { cookie } : {}
  } catch {
    return {}
  }
}
